/**
 * Detects browser-side page translation — Google Translate as used by Chrome
 * and by the Google app's in-app browser on iOS.
 *
 * Two independent signals, because they appear at different moments:
 *
 * - `translated-ltr` / `translated-rtl` on `<html>`: set once a translation
 *   has actually been applied to the DOM.
 * - `window.google.translate` / `window.cr.googleTranslate`: the injected
 *   translate machinery. Present from the moment the browser starts
 *   translating — before the root class, and before the script's own setup
 *   has finished. ENG-2752 is that setup overflowing the stack, so a hook that
 *   only looked at the root class would miss it.
 */

export type TranslationState =
  | 'translated_ltr'
  | 'translated_rtl'
  | 'injected'
  | 'suppressed'
  | 'not_detected'
  | 'unavailable';

interface TranslateGlobals {
  google?: { translate?: unknown };
  cr?: { googleTranslate?: unknown };
}

export const getTranslationState = (): TranslationState => {
  if (typeof document === 'undefined' || !document.documentElement) {
    return 'unavailable';
  }

  const root = document.documentElement;
  if (root.classList.contains('translated-ltr')) return 'translated_ltr';
  if (root.classList.contains('translated-rtl')) return 'translated_rtl';

  const globals = (typeof window === 'undefined'
    ? {}
    : window) as unknown as TranslateGlobals;
  if (globals.google?.translate || globals.cr?.googleTranslate) {
    return 'injected';
  }

  if (
    root.classList.contains('notranslate') ||
    root.getAttribute('translate') === 'no'
  ) {
    return 'suppressed';
  }

  return 'not_detected';
};

/** True while Google Translate's script is in the page, applied or not. */
export const isGoogleTranslateActive = (): boolean => {
  const state = getTranslationState();
  return (
    state === 'translated_ltr' ||
    state === 'translated_rtl' ||
    state === 'injected'
  );
};

export interface BrowserTranslationContext {
  documentLanguage: string;
  translationState: TranslationState;
  navigatorLanguage: string;
  navigatorLanguages: string[];
}

const unavailableContext = (): BrowserTranslationContext => ({
  documentLanguage: 'unknown',
  translationState: 'unavailable',
  navigatorLanguage: 'unknown',
  navigatorLanguages: [],
});

export const collectBrowserTranslationContext =
  (): BrowserTranslationContext => {
    try {
      if (typeof document === 'undefined') return unavailableContext();

      const nav = typeof navigator === 'undefined' ? undefined : navigator;

      return {
        documentLanguage: document.documentElement?.lang || 'unknown',
        translationState: getTranslationState(),
        navigatorLanguage: nav?.language || 'unknown',
        navigatorLanguages: Array.isArray(nav?.languages)
          ? nav.languages.slice(0, 5)
          : [],
      };
    } catch {
      return unavailableContext();
    }
  };
